/**
 * payrollValidations.js
 *
 * Yup validation schemas and helper functions for the configurable payroll system.
 * All schemas validate the TemplateBuilder form state and individual component shapes.
 *
 * @module payrollValidations
 */

import * as Yup from "yup";
import {
  detectCycle,
  canActivate,
  canEditBatch,
  canInitiateRun,
  computeAllocation,
} from "./payrollCalculations";

// ---------------------------------------------------------------------------
// Re-exports from payrollCalculations
// ---------------------------------------------------------------------------

export { canActivate, canEditBatch, canInitiateRun };

// ---------------------------------------------------------------------------
// Earning component schema
// ---------------------------------------------------------------------------

/**
 * Validates a single earning component object.
 *
 * - `id`: required string
 * - `name`: required string, 1–100 chars
 * - `calculationType`: required, one of "fixed" | "percentage"
 * - `fixedAmount`: required and non-negative when calculationType === "fixed"
 * - `percentage`: required, 0–100 when calculationType === "percentage"
 * - `baseComponentId`: required when calculationType === "percentage"
 * - `isTaxable`: required boolean
 */
export const earningComponentSchema = Yup.object().shape({
  id: Yup.string().required("Component ID is required"),

  name: Yup.string()
    .required("Component name is required")
    .min(1, "Name must be at least 1 character")
    .max(100, "Name cannot exceed 100 characters"),

  calculationType: Yup.string()
    .required("Calculation type is required")
    .oneOf(
      [
        "fixed",
        "percentage",
        "percentage_of_gross",
        "percentage_of_component",
        "percentage_of_ctc",
        "residual",
      ],
      "Invalid calculation type",
    ),

  fixedAmount: Yup.number().when("calculationType", {
    is: "fixed",
    then: (schema) =>
      schema
        .required("Amount is required for fixed components")
        .min(0, "Amount cannot be negative"),
    otherwise: (schema) => schema.optional().nullable(),
  }),

  percentage: Yup.number().when("calculationType", {
    is: (v) =>
      [
        "percentage",
        "percentage_of_gross",
        "percentage_of_component",
        "percentage_of_ctc",
      ].includes(v),
    then: (schema) =>
      schema
        .required("Percentage is required")
        .min(0, "Percentage cannot be negative")
        .max(100, "Percentage cannot exceed 100"),
    otherwise: (schema) => schema.optional().nullable(),
  }),

  baseComponentId: Yup.string().when("calculationType", {
    is: (v) => v === "percentage" || v === "percentage_of_component",
    then: (schema) => schema.required("Base component is required for percentage type"),
    otherwise: (schema) => schema.optional().nullable(),
  }),

  isTaxable: Yup.boolean().required("Taxable flag is required"),
});

// ---------------------------------------------------------------------------
// Deduction component schema (custom deductions)
// ---------------------------------------------------------------------------

/**
 * Validates a single custom deduction component object.
 *
 * - `id`: required string
 * - `name`: required string, min 1 char
 * - `calculationType`: required, one of "fixed" | "percentage_of_gross"
 * - `fixedAmount`: non-negative when calculationType === "fixed"
 * - `percentage`: 0–100 when calculationType === "percentage_of_gross"
 */
export const deductionComponentSchema = Yup.object().shape({
  id: Yup.string().required("Component ID is required"),

  name: Yup.string()
    .required("Component name is required")
    .min(1, "Name must be at least 1 character"),

  calculationType: Yup.string()
    .required("Calculation type is required")
    .oneOf(
      ["fixed", "percentage_of_gross"],
      "Calculation type must be fixed or percentage_of_gross",
    ),

  fixedAmount: Yup.number().when("calculationType", {
    is: "fixed",
    then: (schema) => schema.optional().nullable().min(0, "Amount cannot be negative"),
    otherwise: (schema) => schema.optional().nullable(),
  }),

  percentage: Yup.number().when("calculationType", {
    is: "percentage_of_gross",
    then: (schema) =>
      schema
        .optional()
        .nullable()
        .min(0, "Percentage cannot be negative")
        .max(100, "Percentage cannot exceed 100"),
    otherwise: (schema) => schema.optional().nullable(),
  }),
});

// ---------------------------------------------------------------------------
// PF config schema
// ---------------------------------------------------------------------------

/**
 * Validates the Provident Fund configuration block.
 *
 * - `enabled`: boolean
 * - `employeePercent`: 0–100, default 12
 * - `employerPercent`: 0–100, default 12
 */
export const pfConfigSchema = Yup.object().shape({
  enabled: Yup.boolean(),

  employeePercent: Yup.number()
    .min(0, "Employee PF percentage cannot be negative")
    .max(100, "Employee PF percentage cannot exceed 100")
    .default(12),

  employerPercent: Yup.number()
    .min(0, "Employer PF percentage cannot be negative")
    .max(100, "Employer PF percentage cannot exceed 100")
    .default(12),
});

// ---------------------------------------------------------------------------
// ESIC config schema
// ---------------------------------------------------------------------------

/**
 * Validates the ESIC configuration block.
 *
 * - `enabled`: boolean
 * - `employeePercent`: 0–100, default 0.75
 * - `eligibilityCeiling`: min 0, default 21000
 */
export const esicConfigSchema = Yup.object().shape({
  enabled: Yup.boolean(),

  employeePercent: Yup.number()
    .min(0, "ESIC employee percentage cannot be negative")
    .max(100, "ESIC employee percentage cannot exceed 100")
    .default(0.75),

  eligibilityCeiling: Yup.number().min(0, "Eligibility ceiling cannot be negative").default(21000),
});

// ---------------------------------------------------------------------------
// Main template schema (draft / save)
// ---------------------------------------------------------------------------

/**
 * Validates the full TemplateBuilder form for saving as draft.
 * Does NOT enforce a minimum of 1 earning component — use `activatingTemplateSchema`
 * when the user intends to activate the template.
 */
export const templateSchema = Yup.object().shape({
  name: Yup.string()
    .required("Template name is required")
    .min(1, "Template name must be at least 1 character")
    .max(200, "Template name cannot exceed 200 characters")
    .trim(),

  description: Yup.string()
    .optional()
    .nullable()
    .max(500, "Description cannot exceed 500 characters"),

  sampleGrossAmount: Yup.number().min(1000, "Sample amount must be at least ₹1,000").default(50000),

  earningComponents: Yup.array().of(earningComponentSchema).min(0),

  deductionComponents: Yup.object().shape({
    pf: pfConfigSchema,
    esic: esicConfigSchema,
    pt: Yup.object().shape({
      enabled: Yup.boolean(),
    }),
    tds: Yup.object().shape({
      enabled: Yup.boolean(),
    }),
    custom: Yup.array().of(deductionComponentSchema),
  }),

  assignedRoles: Yup.array().of(Yup.number()).optional().nullable(),
});

// ---------------------------------------------------------------------------
// Activating template schema (save & activate)
// ---------------------------------------------------------------------------

/**
 * Extends `templateSchema` to enforce that at least one earning component is
 * present. Used when the user clicks "Save & Activate".
 */
export const activatingTemplateSchema = templateSchema.shape({
  earningComponents: Yup.array()
    .of(earningComponentSchema)
    .min(1, "At least one earning component is required to activate this template"),
});

// ---------------------------------------------------------------------------
// Helper: validateTemplate
// ---------------------------------------------------------------------------

/**
 * Validates a template form values object against the appropriate schema.
 *
 * @param {object} values - Formik form values for the template
 * @param {boolean} [isActivating=false] - When true, uses `activatingTemplateSchema`
 *   which requires at least one earning component
 * @returns {Promise<{ isValid: boolean, errors: object }>} Validation result
 */
export async function validateTemplate(values, isActivating = false) {
  const schema = isActivating ? activatingTemplateSchema : templateSchema;
  try {
    await schema.validate(values, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (err) {
    if (err.name === "ValidationError") {
      const errors = {};
      for (const validationError of err.inner) {
        if (validationError.path && !(validationError.path in errors)) {
          errors[validationError.path] = validationError.message;
        }
      }
      return { isValid: false, errors };
    }
    // Re-throw unexpected errors
    throw err;
  }
}

// ---------------------------------------------------------------------------
// Helper: getCircularDependencyError
// ---------------------------------------------------------------------------

/**
 * Checks the earning components array for circular dependencies.
 *
 * @param {import('./payrollCalculations').EarningComponent[]} components - Array of earning components
 * @returns {string|null} An error message string if a cycle is detected, or null if the
 *   dependency graph is acyclic
 */
export function getCircularDependencyError(components) {
  if (!components || components.length === 0) return null;
  const hasCycle = detectCycle(components);
  if (!hasCycle) return null;
  return "Circular dependency detected in earning components. Please check percentage-based components.";
}

// ---------------------------------------------------------------------------
// NEW — Duplicate name validation
// ---------------------------------------------------------------------------

/**
 * Validates that no two components share the same name (case-insensitive trim).
 *
 * @param {EarningComponent[]} components
 * @returns {string|null} Error message or null
 */
export function validateNoDuplicateNames(components) {
  if (!components || components.length === 0) return null;
  const seen = new Set();
  for (const c of components) {
    const key = (c.name || "").trim().toLowerCase();
    if (!key) continue;
    if (seen.has(key)) return `Duplicate component name: "${c.name}"`;
    seen.add(key);
  }
  return null;
}

// ---------------------------------------------------------------------------
// NEW — Allocation balance validation
// ---------------------------------------------------------------------------

/**
 * Validates that total salary allocation equals 100% (or a residual component exists).
 *
 * @param {EarningComponent[]} components
 * @param {number} referenceSalary
 * @returns {string|null} Error message or null
 */
export function validateAllocation(components, referenceSalary) {
  if (!components || components.length === 0) return null;

  const hasResidual = components.some((c) => c.calculationType === "residual" || c.isResidual);
  if (hasResidual) return null; // residual auto-balances

  const { totalPercent } = computeAllocation(components, referenceSalary);

  if (totalPercent > 100.01) {
    return `Total allocation is ${totalPercent.toFixed(1)}% — exceeds 100%. Reduce component values or add a residual component.`;
  }
  if (totalPercent < 99.99) {
    return `Total allocation is ${totalPercent.toFixed(1)}% — ${(100 - totalPercent).toFixed(1)}% of salary is unallocated. Add a residual component (e.g. Special Allowance) to absorb the remainder.`;
  }
  return null;
}

// Re-export computeAllocation so callers don't need two imports
export { computeAllocation } from "./payrollCalculations";
